/**
 * Zoo discovery / onboarding flow.
 *
 * On first launch the user identifies their zoo via one of:
 *   1. Work email (primary) — user_directory + domain match
 *   2. Join code (secondary) — IT-provided code like "ZL-2026"
 *   3. QR code scan (tertiary) — resolves to slug
 *   4. Slug (hidden fallback) — direct zoo slug
 *
 * The Platform API resolves any of these to a ZooConfig which is stored
 * locally so subsequent launches skip onboarding.
 */

const ZOO_CONFIG_KEY = 'zbk3_zoo_config';

// Staging (SUPABASE_FORCE=true) uses the Platform staging instance;
// production uses the production Platform instance.
const isStaging = process.env.SUPABASE_FORCE === 'true';

const PLATFORM_URL = isStaging
  ? process.env.PLATFORM_SUPABASE_URL_STAGING || ''
  : process.env.PLATFORM_SUPABASE_URL || '';

const PLATFORM_ANON_KEY = isStaging
  ? process.env.PLATFORM_SUPABASE_ANON_KEY_STAGING || ''
  : process.env.PLATFORM_SUPABASE_ANON_KEY || '';

export interface ZooConfig {
  slug: string;
  name: string;
  logoUrl: string | null;
  supabaseUrl: string;
  supabaseAnonKey: string;
  orgId: string;
}

/**
 * Parse the standard Platform API response into a ZooConfig.
 */
function parseZooConfig(data: Record<string, unknown>): ZooConfig {
  return {
    slug: data.slug as string,
    name: data.name as string,
    logoUrl: (data.logo_url as string) ?? null,
    supabaseUrl: data.supabase_url as string,
    supabaseAnonKey: data.supabase_anon_key as string,
    orgId: data.org_id as string,
  };
}

/**
 * Generic resolve call — appends a single query param.
 *
 * Both staging and production use the Platform API for zoo resolution.
 * PLATFORM_URL and PLATFORM_ANON_KEY are set above based on SUPABASE_FORCE.
 */
async function resolveWith(param: string, value: string): Promise<ZooConfig> {
  const response = await fetch(
    `${PLATFORM_URL}/functions/v1/resolve?${param}=${encodeURIComponent(value)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${PLATFORM_ANON_KEY}`,
        apikey: PLATFORM_ANON_KEY,
      },
    },
  );

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error((body as Record<string, string>).error ?? 'Zoo not found');
  }

  const data = await response.json();
  return parseZooConfig(data as Record<string, unknown>);
}

/**
 * Resolve a zoo by its slug via the Platform Edge Function.
 */
export function resolveZoo(slug: string): Promise<ZooConfig> {
  return resolveWith('slug', slug);
}

/**
 * Resolve a zoo by user email via the Platform Edge Function.
 * Checks user_directory first, then falls back to domain matching.
 */
export function resolveZooByEmail(email: string): Promise<ZooConfig> {
  return resolveWith('email', email);
}

/**
 * Resolve a zoo by join code (e.g. "ZL-2026") via the Platform Edge Function.
 */
export function resolveZooByCode(code: string): Promise<ZooConfig> {
  return resolveWith('code', code);
}

/**
 * Store zoo config in localStorage after successful resolution.
 * Platform staging returns the correct staging zoo URL, so no override needed.
 */
export function saveZooConfig(config: ZooConfig): void {
  localStorage.setItem(ZOO_CONFIG_KEY, JSON.stringify(config));
}

/**
 * Read stored zoo config. Returns null if onboarding hasn't been completed.
 */
export function getZooConfig(): ZooConfig | null {
  const raw = localStorage.getItem(ZOO_CONFIG_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ZooConfig;
  } catch {
    return null;
  }
}

/**
 * Clear stored zoo config (used when switching zoos).
 */
export function clearZooConfig(): void {
  localStorage.removeItem(ZOO_CONFIG_KEY);
}
